import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FilterX, Images } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState, ErrorState } from '@/components/states';
import { SingleFacetFilter, toFacetOptions } from '@/features/links/facet-filter';
import { MediaTile } from '@/features/media/media-tile';
import { MediaViewerDialog } from '@/features/media/media-viewer-dialog';
import { useMediaFacetsQuery, useMediaListQuery } from '@/hooks/use-media';
import { useIsAdmin } from '@/lib/current-user';
import { pluralize } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { ListMediaParams, MediaFacetOption, MediaItem, MediaKind } from '@/types/api';

const GRID_CLASSES = 'grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5';

const KIND_FILTERS: ReadonlyArray<{ value: MediaKind | null; label: string }> = [
  { value: null, label: 'All' },
  { value: 'image', label: 'Images' },
  { value: 'video', label: 'Videos' },
];

function parseKind(value: string | null): MediaKind | null {
  return value === 'image' || value === 'video' ? value : null;
}

/** What the current view holds, for the empty state and the end-of-list line. */
function nounFor(kind: MediaKind | null): string {
  return kind === 'image' ? 'image' : kind === 'video' ? 'video' : 'file';
}

/**
 * Images and videos shared in the Slack channels an administrator opted in to. Behind sign-in
 * whatever the catalogue's visibility (RequireSignedIn in app.tsx; the API refuses anonymous
 * callers too).
 *
 * The filters live in the query string (`?channel=…&sharer=…&type=video`), so the statistics page
 * can link straight to one channel's or one person's files and a filtered view can be bookmarked.
 */
export function MediaPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const channelId = searchParams.get('channel');
  const sharerId = searchParams.get('sharer');
  const kind = parseKind(searchParams.get('type'));
  const isAdmin = useIsAdmin();

  const params = useMemo<ListMediaParams>(
    () => ({
      ...(channelId ? { channelId } : {}),
      ...(sharerId ? { sharerId } : {}),
      ...(kind ? { kind } : {}),
    }),
    [channelId, sharerId, kind],
  );

  const media = useMediaListQuery(params);
  const facets = useMediaFacetsQuery();
  const mediaCount = (facet: MediaFacetOption) => facet.mediaCount;
  const channelOptions = useMemo(
    () => toFacetOptions(facets.data?.channels, mediaCount),
    [facets.data?.channels],
  );
  const sharerOptions = useMemo(
    () => toFacetOptions(facets.data?.sharers, mediaCount),
    [facets.data?.sharers],
  );
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const items: MediaItem[] = useMemo(
    () => media.data?.pages.flatMap((page) => page.items) ?? [],
    [media.data],
  );

  const setFilter = (key: 'channel' | 'sharer' | 'type', value: string | null) => {
    setOpenIndex(null);
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  };

  const clearFilters = () => {
    setOpenIndex(null);
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const filtered = Boolean(channelId || sharerId || kind);
  const noun = nounFor(kind);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight">Media</h1>
        <p className="max-w-2xl text-sm text-muted-foreground">
          Images and videos shared in the Slack channels chosen for media collection. Each distinct
          file is kept once, credited to whoever shared it first.
        </p>
      </header>

      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <div
          role="group"
          aria-label="Type"
          className="inline-flex h-9 w-fit items-center rounded-md border border-input bg-background p-0.5"
        >
          {KIND_FILTERS.map((option) => {
            const active = kind === option.value;
            return (
              <button
                key={option.label}
                type="button"
                aria-pressed={active}
                onClick={() => setFilter('type', option.value)}
                className={cn(
                  'h-full rounded px-3 text-sm transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  active
                    ? 'bg-accent font-medium text-accent-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        <SingleFacetFilter
          label="Channel"
          options={channelOptions}
          loading={facets.isPending}
          emptyMessage="No channels with images or videos yet."
          countUnit="file"
          selected={channelId}
          onChange={(value) => setFilter('channel', value)}
          className="sm:w-64"
        />
        <SingleFacetFilter
          label="Shared by"
          options={sharerOptions}
          loading={facets.isPending}
          emptyMessage="Nobody has shared an image or video yet."
          countUnit="file"
          selected={sharerId}
          onChange={(value) => setFilter('sharer', value)}
          className="sm:w-64"
        />
        {filtered ? (
          <Button type="button" variant="ghost" size="sm" onClick={clearFilters}>
            <FilterX aria-hidden="true" className="h-4 w-4" />
            Clear filters
          </Button>
        ) : null}
      </div>

      <p role="status" aria-live="polite" className="sr-only">
        {media.isPending
          ? 'Loading images and videos'
          : media.isError
            ? 'Could not load images and videos.'
            : `${items.length} ${pluralize(items.length, noun)} shown.`}
      </p>

      {media.isError ? (
        <ErrorState
          title="Could not load the images and videos"
          error={media.error}
          onRetry={() => void media.refetch()}
        />
      ) : media.isPending ? (
        <ul className={GRID_CLASSES} aria-hidden="true">
          {Array.from({ length: 10 }, (_, index) => (
            <li key={index}>
              <Skeleton className="aspect-square w-full" />
            </li>
          ))}
        </ul>
      ) : items.length === 0 ? (
        filtered ? (
          <EmptyState
            icon={<FilterX aria-hidden="true" className="h-8 w-8" />}
            title={`No ${noun}s match these filters`}
            action={
              <Button type="button" variant="outline" onClick={clearFilters}>
                Clear filters
              </Button>
            }
          />
        ) : (
          <EmptyState
            icon={<Images aria-hidden="true" className="h-8 w-8" />}
            title="No images or videos yet"
            description={
              isAdmin
                ? 'Choose which Slack channels to collect images and videos from, and what is shared there from then on will appear here.'
                : 'An administrator chooses which Slack channels images and videos are collected from. What is shared there will appear here.'
            }
            action={
              isAdmin ? (
                <Button asChild variant="outline">
                  <Link to="/settings">Choose channels</Link>
                </Button>
              ) : undefined
            }
          />
        )
      ) : (
        <>
          <ul className={GRID_CLASSES}>
            {items.map((item, index) => (
              <li key={item.mediaId}>
                <MediaTile media={item} onOpen={() => setOpenIndex(index)} />
              </li>
            ))}
          </ul>

          <div className="flex justify-center pt-2">
            {media.hasNextPage ? (
              <Button
                type="button"
                variant="outline"
                loading={media.isFetchingNextPage}
                onClick={() => void media.fetchNextPage()}
              >
                {media.isFetchingNextPage ? 'Loading more…' : 'Load more'}
              </Button>
            ) : (
              <p className="text-xs text-muted-foreground">
                That is every {noun} {filtered ? 'matching this view' : 'collected so far'}.
              </p>
            )}
          </div>
        </>
      )}

      <MediaViewerDialog
        items={items}
        index={openIndex !== null && openIndex < items.length ? openIndex : null}
        onIndexChange={setOpenIndex}
        onClose={() => setOpenIndex(null)}
        canManage={isAdmin}
      />
    </div>
  );
}
